import { useContext } from "react";

import { UserContext } from "../../contexts";
import { getUkStoreIdM } from "../config/storeId.js";

/**
 * The `store_id_m` the CURRENT request is about, for the handful of endpoints
 * that still take one as a parameter.
 *
 * Almost nothing in the app needs this. Scope normally travels as the
 * `X-Active-Store` header (core/config/activeStore.js) and the server decides,
 * via App\Support\TenantContext — pages do not think about stores at all. The
 * Core List family is the exception: it was built before the store switcher
 * existed and passes `store_id_m` as a query parameter instead.
 *
 * Order of preference:
 *   1. the store selected in the switcher,
 *   2. the user's own store, for anyone who has not switched,
 *   3. the env default (DAO UK) — RC master with no selection only; anyone
 *      else gets null (L12). A half-provisioned invitee with no store used to
 *      land here too, and because the server treats a non-master with no
 *      store as unscoped, it honoured the DAO id and served DAO's Core List.
 *      Every caller already handles null (query disabled / nothing written).
 *
 * Each of those three callers previously started at step 2 or step 3, so the
 * switcher silently did nothing to them: CoreListTemplate.jsx resolved
 * getUkStoreIdM() ONCE at module load — 873 in production — and served, and
 * wrote, DAO Watches UK under every store (Alex, 2026-10-06).
 *
 * The server no longer trusts this value: CoreListController::effectiveStoreId()
 * asks TenantContext first and honours what is sent here only for a genuinely
 * unscoped master. So this hook is now about the page agreeing with the server,
 * not about enforcement — which is the right way round.
 */
export const useActiveStoreIdM = () => {
  const { activeStoreId, user } = useContext(UserContext) ?? {};

  return activeStoreId ?? user?.store_id_m ?? (user?.is_master_admin ? getUkStoreIdM() : null);
};

export default useActiveStoreIdM;
